/**
 * ChartCardWidget — a config-driven chart bound to the shared DashboardContext.
 *
 * The widget config supplies a `fetcher({ siteId, time, departmentId, signal })`
 * that returns `{ data, series }`. Decoupling data-fetching from ChartWidget
 * lets a chart pull from ANY endpoint (the legacy /dashboard/safety/trends, a
 * future /v2/series, etc.) without changing the widget.
 */
import React from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import ChartWidget from './ChartWidget';
import { isSiteRequiredError, SiteRequiredNotice } from '../utils/siteScope';
import { CARD, HEADING } from '../ui';

export default function ChartCardWidget({ title, chart = {}, fetcher }) {
  const ctx = useDashboard();
  const { siteId, time, departmentId, dimensions, standard, refreshMs } = ctx;

  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';
  const { data, loading, error } = useDashboardData(
    (signal) => fetcher({ siteId, time, departmentId, dimensions, standard, signal }),
    [siteId, timeKey, departmentId, dimsKey, standard],
    { enabled: typeof fetcher === 'function', refreshMs },
  );

  // Not a failure — the server is asking which site to read. Saying "Failed to
  // load chart" here sent the reader looking for a broken chart instead of the
  // site selector.
  if (isSiteRequiredError(error)) {
    return (
      <div className={`${CARD} p-4`} style={chart.height ? { minHeight: chart.height } : undefined}>
        {title && <h3 className={`mb-2 ${HEADING}`}>{title}</h3>}
        <SiteRequiredNotice />
      </div>
    );
  }

  return (
    <ChartWidget
      type={chart.type}
      data={data?.data || []}
      xKey={chart.xKey}
      series={data?.series || chart.series || []}
      title={title}
      height={chart.height}
      valueFormat={chart.valueFormat}
      onPointClick={chart.onPointClick}
      loading={loading}
      error={error}
    />
  );
}
